import React, { FC } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { scale } from '@components/frame';
import { CollapsedSection as CollapsedSectionType, DiffRowProps } from './types';
import DiffLine from './DiffLine';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { isPhone, isWeb } from '@helpers';
import { JUMP_HIGHLIGHT_BORDER_RADIUS, JUMP_HIGHLIGHT_COLOR } from './constants/diffView.constants';
import { useJumpHighlightOpacity } from './hooks/useJumpHighlightOpacity';

const DiffRow: FC<DiffRowProps> = ({
  C,
  oldLine,
  newLine,
  isGroupStart,
  isGroupEnd,
  isFirstLineInDocument,
  groupPositionOld,
  groupPositionNew,
  nativeID,
  highlightTick,
}) => {
  const isTablet = !isPhone && !isWeb;
  const sectionGap = scale(16);
  const highlightOpacity = useJumpHighlightOpacity(highlightTick);

  return (
    <View nativeID={nativeID} style={[styles.rowContainer, isTablet && styles.tabletRowContainer]}>
      <View style={[styles.columnWrapper, { marginRight: sectionGap }]}>
        <View
          style={[
            styles.cardPart,
            isGroupStart && styles.cardTop,
            isGroupEnd && styles.cardBottom,
            { backgroundColor: C('Background/Light/Field') },
          ]}
        >
          {oldLine ? (
             <DiffLine
               C={C}
               line={oldLine}
               isOldVersion={true}
               groupPosition={groupPositionOld}
               isFirstLineInDocument={isFirstLineInDocument}
             />
          ) : null}
        </View>
      </View>

      <View style={styles.columnWrapper}>
        <View
          style={[
            styles.cardPart,
            isGroupStart && styles.cardTop,
            isGroupEnd && styles.cardBottom,
            { backgroundColor: C('Background/Light/Field') },
          ]}
        >
           {newLine ? (
             <DiffLine
               C={C}
               line={newLine}
               isOldVersion={false}
               groupPosition={groupPositionNew}
               isFirstLineInDocument={isFirstLineInDocument}
             />
          ) : null}
        </View>
      </View>

      {!isWeb && (
        <Animated.View
          pointerEvents="none"
          style={[styles.highlightOverlay, { opacity: highlightOpacity }]}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  rowContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  tabletRowContainer: {
    marginTop: -1, // temporary fix for sub-pixel gaps on tablets, will be removed after refactoring
  },
  columnWrapper: {
    flex: 1,
  },
  cardPart: {
    flex: 1,
  },
  cardTop: {
    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
  },
  cardBottom: {
    borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
  },
  highlightOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: JUMP_HIGHLIGHT_COLOR,
    borderRadius: scale(JUMP_HIGHLIGHT_BORDER_RADIUS),
  },
});

export default React.memo(DiffRow);
